import { create } from 'zustand';
import type { PlatformAuthScope, PlatformRole, PlatformUserInfo } from '@/models/auth';

interface DemoUser {
  email: string;
  role: PlatformRole;
}

interface AuthState {
  user: unknown;
  userInfo: PlatformUserInfo | null;
  loading: boolean;
  demoUser: DemoUser | null;
  availableScopes: PlatformAuthScope[];
  activeScope: PlatformAuthScope | null;
  activeScopeKey: string | null;
  setUser: (user: unknown) => void;
  setUserInfo: (info: PlatformUserInfo | null) => void;
  setLoading: (loading: boolean) => void;
  setDemoUser: (user: DemoUser) => void;
  setAvailableScopes: (scopes: PlatformAuthScope[]) => void;
  setActiveScope: (scope: PlatformAuthScope | null) => void;
  clearScopes: () => void;
  clearDemoUser: () => void;
  signOut: () => void;
}

const DEMO_USER_KEY = 'admin_portal_demo_user';
const ACTIVE_SCOPE_KEY = 'admin_portal_active_scope';

function loadDemoUser(): DemoUser | null {
  try {
    const raw = sessionStorage.getItem(DEMO_USER_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as { email: string; role?: string };
    if (parsed.role === 'platform_admin' || parsed.role === 'org_admin') {
      return {
        email: parsed.email,
        role: parsed.role,
      };
    }

    return null;
  } catch {
    return null;
  }
}

function saveDemoUser(user: DemoUser | null) {
  if (user) {
    sessionStorage.setItem(DEMO_USER_KEY, JSON.stringify(user));
  } else {
    sessionStorage.removeItem(DEMO_USER_KEY);
  }
}

function loadActiveScopeKey(): string | null {
  try {
    return sessionStorage.getItem(ACTIVE_SCOPE_KEY);
  } catch {
    return null;
  }
}

function saveActiveScopeKey(scopeKey: string | null) {
  if (scopeKey) {
    sessionStorage.setItem(ACTIVE_SCOPE_KEY, scopeKey);
  } else {
    sessionStorage.removeItem(ACTIVE_SCOPE_KEY);
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  userInfo: null,
  loading: true,
  demoUser: loadDemoUser(),
  availableScopes: [],
  activeScope: null,
  activeScopeKey: loadActiveScopeKey(),
  setUser: (user) => set({ user }),
  setUserInfo: (userInfo) => set({ userInfo }),
  setLoading: (loading) => set({ loading }),
  setDemoUser: (demoUser) => {
    saveDemoUser(demoUser);
    set({ demoUser });
  },
  setAvailableScopes: (availableScopes) => set({ availableScopes }),
  setActiveScope: (activeScope) => {
    saveActiveScopeKey(activeScope?.scopeKey ?? null);
    set({
      activeScope,
      activeScopeKey: activeScope?.scopeKey ?? null,
    });
  },
  clearScopes: () => {
    saveActiveScopeKey(null);
    set({
      availableScopes: [],
      activeScope: null,
      activeScopeKey: null,
    });
  },
  clearDemoUser: () => {
    saveDemoUser(null);
    set({ demoUser: null });
  },
  signOut: () => {
    saveDemoUser(null);
    saveActiveScopeKey(null);
    set({
      user: null,
      userInfo: null,
      demoUser: null,
      availableScopes: [],
      activeScope: null,
      activeScopeKey: null,
    });
  },
}));
